import { useQuery } from "@tanstack/react-query"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { languages } from "lopermut"
import { useGetBackofficeCompaniesQuery } from "modules/backoffice/hooks/companies/useGetBackofficeCompaniesQuery"
import { BACKOFFICE_RECENT_TIME_FRAME_OPTIONS } from "modules/backoffice/lib/backofficeRecentTimeFrameOptions"
import { TimeRangeSelector } from "modules/shared/components/TimeRange/TimeRangeSelector"
import { getBackofficeConversationExperimentAnalyticsQuery } from "modules/backoffice/lib/experiments/getBackofficeConversationExperimentAnalyticsQuery"
import { getBackofficeConversationExperimentKnowledgeAnalyticsQuery } from "modules/backoffice/lib/experiments/getBackofficeConversationExperimentKnowledgeAnalyticsQuery"
import { getBackofficeConversationExperimentDomainsQuery } from "modules/backoffice/lib/experiments/getBackofficeConversationExperimentDomainsQuery"
import { getBackofficeConversationExperimentNamesQuery } from "modules/backoffice/lib/experiments/getBackofficeConversationExperimentNamesQuery"
import { getBackofficeConversationExperimentsQuery } from "modules/backoffice/lib/experiments/getBackofficeConversationExperimentsQuery"
import { Pagination } from "modules/shared/components/Pagination"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Route } from "routes/_base/_auth/backoffice/conversationExperiments/list/route"
import {
  ChannelEnum,
  ConversationExperimentProductionName,
  Flow,
  FlowEnum,
  GetBackofficeConversationExperimentsSchema,
  OrganizationTypeEnum,
} from "schema"
import { z } from "zod"
import { BackofficeConversationExperimentAnalyticsChart } from "./BackofficeConversationExperimentAnalyticsChart"
import { BackofficeConversationExperimentKnowledgeTable } from "./BackofficeConversationExperimentKnowledgeTable"
import { BackofficeConversationExperimentsMultiSelect } from "./BackofficeConversationExperimentsMultiSelect"
import { BackofficeConversationExperimentsFilters } from "./BackofficeConversationExperimentsFilters"
import { BackofficeConversationExperimentsTable } from "./BackofficeConversationExperimentsTable"

const ITEMS_PER_PAGE = 50
const COMPANY_OPTIONS_LIMIT = 100
const LANGUAGE_OPTIONS = [
  ...new Map(
    languages.map(({ label, value }) => {
      const language = value.replace(/_.*/, "")
      return [language, { value: language, label: label.replace(/ \(.*\)$/, "") }]
    }),
  ).values(),
]

type Props = {
  search: z.infer<typeof GetBackofficeConversationExperimentsSchema>
  routeFullPath: string
}

export const BackofficeConversationExperiments = ({ search, routeFullPath }: Props) => {
  const { t } = useTranslation()
  const navigate = Route.useNavigate()
  const [companySearch, setCompanySearch] = useState("")
  const debouncedCompanySearch = useDebouncedValue({ value: companySearch })

  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath: routeFullPath,
    total: ITEMS_PER_PAGE,
  })

  const analyticsQuery = {
    experimentDomain: search.experimentDomain,
    campaignFlow: search.campaignFlow,
    companyIds: search.companyIds,
    channels: search.channels,
    locales: search.locales,
    hasQuestions: search.hasQuestions,
    hasTicket: search.hasTicket,
    timeFrame: search.timeFrame,
  }

  const listQuery = {
    ...analyticsQuery,
    page,
    total: ITEMS_PER_PAGE,
    excludeProduction: search.excludeProduction,
    experiment: search.experiment,
  }

  const queryOptions = getBackofficeConversationExperimentsQuery({
    query: listQuery,
    onSuccess: (data) => {
      setCount(data.pageInfo.count)
    },
  })

  const { data, isPending } = useQuery(queryOptions)
  const { data: experimentDomains } = useQuery(getBackofficeConversationExperimentDomainsQuery())
  const { data: experimentNames } = useQuery(
    getBackofficeConversationExperimentNamesQuery({
      query: { experimentDomain: search.experimentDomain },
    }),
  )
  const { data: analytics, isPending: isAnalyticsPending } = useQuery(
    getBackofficeConversationExperimentAnalyticsQuery({ query: analyticsQuery }),
  )
  const { data: knowledgeAnalytics, isPending: isKnowledgeAnalyticsPending } = useQuery(
    getBackofficeConversationExperimentKnowledgeAnalyticsQuery({ query: analyticsQuery }),
  )
  const isFiltered = Boolean(
    search.hasQuestions !== undefined ||
    search.hasTicket !== undefined ||
    search.campaignFlow ||
    search.companyIds?.length ||
    search.channels?.length ||
    search.locales?.length,
  )
  const { data: unfilteredKnowledgeAnalytics } = useQuery({
    ...getBackofficeConversationExperimentKnowledgeAnalyticsQuery({
      query: { experimentDomain: search.experimentDomain, timeFrame: search.timeFrame },
    }),
    enabled: isFiltered,
  })

  const { data: companies } = useGetBackofficeCompaniesQuery({
    query: {
      query: debouncedCompanySearch || undefined,
      page: 1,
      total: COMPANY_OPTIONS_LIMIT,
      sortBy: { field: "name", order: "asc" },
      filterBy: { type: [OrganizationTypeEnum.Client, OrganizationTypeEnum.Partner] },
    },
  })

  const companyOptions = useMemo(
    () => (companies ?? []).map(({ id, name }) => ({ value: id, label: name })),
    [companies],
  )

  const channelOptions = useMemo(
    () =>
      Object.values(ChannelEnum).map((channel) => ({
        value: channel,
        label: t(`campaigns.channel.${channel.toLowerCase()}.label`),
      })),
    [t],
  )

  function updateSearch(next: Partial<typeof search>) {
    navigate({
      search: { ...search, ...next, page: 1 },
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={search.experimentDomain ?? "all"}
            onValueChange={(value) =>
              updateSearch({
                experimentDomain: value === "all" ? undefined : value,
                experiment: undefined,
              })
            }
          >
            <SelectTrigger id="experiment-domain" className="h-9 w-44 max-w-full shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("backoffice.conversationExperiments.filters.allDomains")}
              </SelectItem>
              {experimentDomains?.map((domain) => (
                <SelectItem key={domain} value={domain}>
                  {domain}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={search.campaignFlow ?? "all"}
            onValueChange={(value) =>
              updateSearch({ campaignFlow: value === "all" ? undefined : (value as Flow) })
            }
          >
            <SelectTrigger id="campaign-flow" className="h-9 w-44 max-w-full shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("backoffice.conversationExperiments.filters.allFlows")}
              </SelectItem>
              {Object.values(FlowEnum).map((flow) => (
                <SelectItem key={flow} value={flow}>
                  {t(`backoffice.conversationExperiments.filters.flow.${flow.toLowerCase()}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <BackofficeConversationExperimentsMultiSelect
            allLabel={t("backoffice.bi.allCompanies")}
            options={companyOptions}
            values={search.companyIds}
            onChange={(companyIds) => updateSearch({ companyIds })}
            onSearchChange={setCompanySearch}
          />
          <BackofficeConversationExperimentsMultiSelect
            allLabel={t("backoffice.conversationExperiments.filters.allChannels")}
            options={channelOptions}
            values={search.channels}
            onChange={(channels) => updateSearch({ channels })}
          />
          <BackofficeConversationExperimentsMultiSelect
            allLabel={t("backoffice.conversationExperiments.filters.allLocales")}
            options={LANGUAGE_OPTIONS}
            values={search.locales}
            onChange={(locales) => updateSearch({ locales })}
          />
          <Select
            value={search.hasQuestions?.toString() ?? "all"}
            onValueChange={(value) =>
              updateSearch({ hasQuestions: value === "all" ? undefined : value === "true" })
            }
          >
            <SelectTrigger id="conversation-questions" className="h-9 w-44 max-w-full shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("backoffice.conversationExperiments.filters.allConversations")}
              </SelectItem>
              <SelectItem value="true">
                {t("backoffice.conversationExperiments.filters.withQuestions")}
              </SelectItem>
              <SelectItem value="false">
                {t("backoffice.conversationExperiments.filters.withNoQuestions")}
              </SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={search.hasTicket?.toString() ?? "all"}
            onValueChange={(value) =>
              updateSearch({ hasTicket: value === "all" ? undefined : value === "true" })
            }
          >
            <SelectTrigger id="conversation-tickets" className="h-9 w-44 max-w-full shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("backoffice.conversationExperiments.filters.allConversations")}
              </SelectItem>
              <SelectItem value="true">
                {t("backoffice.conversationExperiments.filters.withTicket")}
              </SelectItem>
              <SelectItem value="false">
                {t("backoffice.conversationExperiments.filters.withNoTicket")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <TimeRangeSelector
          value={search.timeFrame}
          onChange={({ timeFrame }) => updateSearch({ timeFrame })}
          presets={BACKOFFICE_RECENT_TIME_FRAME_OPTIONS}
        />
      </div>
      <BackofficeConversationExperimentAnalyticsChart
        data={analytics}
        isPending={isAnalyticsPending}
      />
      <BackofficeConversationExperimentKnowledgeTable
        data={knowledgeAnalytics}
        unfilteredData={isFiltered ? unfilteredKnowledgeAnalytics : undefined}
        isPending={isKnowledgeAnalyticsPending}
      />
      <BackofficeConversationExperimentsFilters
        search={search}
        experimentNames={experimentNames}
        onExcludeProductionChange={(excludeProduction) => {
          const next: Partial<typeof search> = { excludeProduction }
          if (excludeProduction && search.experiment === ConversationExperimentProductionName) {
            next.experiment = undefined
          }
          updateSearch(next)
        }}
        onExperimentChange={(experiment) => updateSearch({ experiment })}
      />
      <BackofficeConversationExperimentsTable data={data} isPending={isPending} />
      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} count={count} />
    </div>
  )
}
